import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Me } from "../api";
import { BookMark } from "../components/icons";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import LandingDemo from "./LandingDemo";

export default function Landing({ me }: { me: Me | null }) {
  const { t } = useTranslation();
  const ctaTo = me ? "/app" : "/login";
  const ctaLabel = me ? t("landing.ctaOpenApp") : t("landing.ctaGetStarted");
  const topbarLabel = me ? t("landing.ctaOpenApp") : t("landing.signIn");

  const steps = [
    { title: t("landing.step1Title"), body: t("landing.step1Body") },
    { title: t("landing.step2Title"), body: t("landing.step2Body") },
    { title: t("landing.step3Title"), body: t("landing.step3Body") },
  ];

  return (
    <div className="tp-app tp-wide">
      <div className="tp-topbar">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <BookMark />
          <div style={{ fontFamily: "var(--tp-font-display)", fontSize: 22, fontWeight: 700, color: "var(--tp-ink)" }}>
            {t("appName")}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <LanguageSwitcher />
          <Link
            to={ctaTo}
            className="tp-btn tp-btn-ghost"
            style={{ padding: "8px 14px", fontSize: 16, textDecoration: "none" }}
          >
            {topbarLabel}
          </Link>
        </div>
      </div>

      <div className="tp-screen" style={{ paddingTop: 24 }}>
        {/* Hero */}
        <div className="tp-rise" style={{ textAlign: "center", maxWidth: 640, margin: "0 auto", animationDelay: "0s" }}>
          <div className="tp-display" style={{ marginBottom: 12, fontSize: 44 }}>{t("landing.heroTitle")}</div>
          <div className="tp-body" style={{ color: "var(--tp-ink-soft)", marginBottom: 22, fontSize: 17, lineHeight: 1.45 }}>
            {t("landing.heroSubtitle")}
          </div>
          <Link
            to={ctaTo}
            className="tp-btn tp-btn-primary"
            style={{ display: "inline-flex", fontSize: 18, padding: "14px 28px", textDecoration: "none" }}
          >
            {ctaLabel}
          </Link>
        </div>

        {/* How it works — steps up top, horizontal on desktop */}
        <section className="tp-landing-section tp-rise" style={{ animationDelay: "0.08s" }}>
          <span className="tp-eyebrow">{t("landing.stepsHeading")}</span>
          <div className="tp-steps" style={{ marginTop: 14 }}>
            {steps.map((s, i) => (
              <div key={i} className="tp-step">
                <div className="tp-step-num">{i + 1}</div>
                <div className="tp-step-title">{s.title}</div>
                <div className="tp-step-body">{s.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Two creation modes */}
        <section className="tp-landing-section tp-rise" style={{ animationDelay: "0.16s" }}>
          <div className="tp-landing-section-title">{t("landing.modesHeading")}</div>
          <div className="tp-landing-section-sub">{t("landing.modesSubheading")}</div>
          <div className="tp-modes">
            <div className="tp-card tp-mode-card" data-accent="photos">
              <span className="tp-mode-tag">{t("landing.mode1Tag")}</span>
              <div className="tp-h2 tp-mode-title">{t("landing.mode1Title")}</div>
              <div className="tp-body tp-mode-body">{t("landing.mode1Body")}</div>
            </div>
            <div className="tp-card tp-mode-card" data-accent="story">
              <span className="tp-mode-tag">{t("landing.mode2Tag")}</span>
              <div className="tp-h2 tp-mode-title">{t("landing.mode2Title")}</div>
              <div className="tp-body tp-mode-body">{t("landing.mode2Body")}</div>
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: 28 }}>
            <Link
              to={ctaTo}
              className="tp-btn tp-btn-primary"
              style={{ display: "inline-flex", fontSize: 18, padding: "14px 28px", textDecoration: "none" }}
            >
              {ctaLabel}
            </Link>
            {!me && (
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <span
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    background: "#F4E7B0", color: "var(--tp-ink)",
                    border: "2px solid var(--tp-ink)", borderRadius: 999,
                    padding: "11px 24px", fontSize: 18, fontWeight: 700,
                    boxShadow: "2px 3px 0 rgba(42,31,18,0.14)",
                  }}
                >
                  🎟️ {t("landing.betaCredits")}
                </span>
              </div>
            )}
          </div>
        </section>

        {/* Live preview of a real book */}
        <section className="tp-landing-section tp-rise" style={{ animationDelay: "0.24s" }}>
          <div className="tp-landing-section-title">{t("landing.previewHeading")}</div>
          <LandingDemo />
        </section>

        {/* Footer */}
        <div className="tp-rise" style={{ textAlign: "center", marginTop: 44, animationDelay: "0.3s" }}>
          <div className="tp-body" style={{ fontSize: 14, color: "var(--tp-ink-soft)" }}>
            {t("landing.footer")} ·{" "}
            <Link to="/legal" style={{ color: "var(--tp-accent)", textDecoration: "underline" }}>
              {t("legal.title")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
